import { useState, useRef, useEffect } from "react";
import type { RestaurantFormData } from "../../restaurant.types";
import { X, Plus, ChefHat, Utensils, Sparkles, Tag, Hash } from "lucide-react";

interface CuisineTagsStepProps {
  formData: RestaurantFormData;
  updateFormData: (updatedFields: Partial<RestaurantFormData>) => void;
}

/* ─── Suggested quick-pick options per category ─── */
const SUGGESTIONS: Record<string, string[]> = {
  cuisine: [
    "Italian", "Japanese", "Indian", "Chinese", "Mexican",
    "Mediterranean", "French", "Thai", "American", "Korean",
    "Middle Eastern", "Greek", "Spanish", "Vietnamese", "Ethiopian",
  ],
  typeOfFood: [
    "Vegetarian", "Vegan", "Gluten-Free", "Halal", "Kosher",
    "Seafood", "Burgers", "Pizza", "Sushi", "BBQ",
    "Salads", "Pasta", "Sandwiches", "Desserts", "Tapas",
  ],
  experience: [
    "Fine Dining", "Casual", "Family-Friendly", "Rooftop",
    "Outdoor Seating", "Live Music", "Sports Bar", "Brunch",
    "Romantic", "Business Lunch", "Buffet", "Fast Casual",
    "Late Night", "Private Dining", "Waterfront",
  ],
  tags: [
    "Cozy", "Trendy", "Hidden Gem", "Pet-Friendly", "BYOB",
    "Happy Hour", "Takeout", "Delivery", "Reservations", "Parking",
    "Date Night", "Group Dining", "Michelin", "Award Winning",
  ],
};

const CATEGORY_CONFIG = [
  {
    key: "cuisine" as keyof RestaurantFormData,
    label: "Cuisine Types",
    sublabel: "What cuisines do you serve?",
    icon: ChefHat,
    required: true,
    color: "amber",
  },
  {
    key: "typeOfFood" as keyof RestaurantFormData,
    label: "Food Types",
    sublabel: "Dietary styles & food categories",
    icon: Utensils,
    required: false,
    color: "amber",
  },
  {
    key: "experience" as keyof RestaurantFormData,
    label: "Dining Experience",
    sublabel: "What kind of experience do you offer?",
    icon: Sparkles,
    required: false,
    color: "amber",
  },
  {
    key: "tags" as keyof RestaurantFormData,
    label: "Tags & Special Features",
    sublabel: "Keywords to help guests discover you",
    icon: Hash,
    required: false,
    color: "amber",
  },
];

const colorMap: Record<string, { chip: string; chipSelected: string; suggestion: string; suggestionSelected: string; ring: string }> = {
  amber: {
    chip: "bg-[#FFD166] text-gray-900 border-[#FFD166]",
    chipSelected: "bg-[#FFD166] text-gray-900",
    suggestion: "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100 hover:border-amber-300",
    suggestionSelected: "bg-amber-500 text-white border-amber-500",
    ring: "focus:ring-[#FFD166]/60 focus:border-[#FFD166]",
  },
  orange: {
    chip: "bg-orange-100 text-orange-900 border-orange-200",
    chipSelected: "bg-orange-400 text-white",
    suggestion: "bg-orange-50 text-orange-800 border-orange-200 hover:bg-orange-100 hover:border-orange-300",
    suggestionSelected: "bg-orange-400 text-white border-orange-400",
    ring: "focus:ring-orange-300 focus:border-orange-400",
  },
  rose: {
    chip: "bg-rose-100 text-rose-900 border-rose-200",
    chipSelected: "bg-rose-400 text-white",
    suggestion: "bg-rose-50 text-rose-800 border-rose-200 hover:bg-rose-100 hover:border-rose-300",
    suggestionSelected: "bg-rose-400 text-white border-rose-400",
    ring: "focus:ring-rose-300 focus:border-rose-400",
  },
  blue: {
    chip: "bg-blue-100 text-blue-900 border-blue-200",
    chipSelected: "bg-blue-500 text-white",
    suggestion: "bg-blue-50 text-blue-800 border-blue-200 hover:bg-blue-100 hover:border-blue-300",
    suggestionSelected: "bg-blue-500 text-white border-blue-500",
    ring: "focus:ring-blue-300 focus:border-blue-400",
  },
};

/* ─── Individual chip ─── */
const Chip = ({
  label,
  onRemove,
  color,
}: {
  label: string;
  onRemove: () => void;
  color: string;
}) => {
  const c = colorMap[color];
  return (
    <span
      className={`inline-flex items-center gap-1.5 pl-3 pr-2 py-1.5 rounded-full text-xs font-semibold border transition-all
        ${c.chipSelected}`}
      style={{ animation: "chipIn 0.15s ease both" }}
    >
      {label}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label}`}
        className="w-4 h-4 rounded-full flex items-center justify-center bg-black/10 hover:bg-black/25 transition-colors flex-shrink-0"
      >
        <X className="w-2.5 h-2.5" />
      </button>
    </span>
  );
};

/* ─── Category section ─── */
const CategorySection = ({
  categoryKey,
  label,
  sublabel,
  icon: Icon,
  required,
  color,
  selectedItems,
  onAdd,
  onRemove,
}: {
  categoryKey: string;
  label: string;
  sublabel: string;
  icon: React.ElementType;
  required: boolean;
  color: string;
  selectedItems: string[];
  onAdd: (value: string) => void;
  onRemove: (value: string) => void;
}) => {
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const c = colorMap[color];

  const suggestions = SUGGESTIONS[categoryKey] || [];
  const filteredSuggestions = inputValue.trim()
    ? suggestions.filter(
        (s) =>
          s.toLowerCase().includes(inputValue.toLowerCase()) &&
          !selectedItems.includes(s)
      )
    : suggestions.filter((s) => !selectedItems.includes(s));

  const handleAdd = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onAdd(trimmed);
    setInputValue("");
    inputRef.current?.focus();
  };

  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden bg-white shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 bg-gray-50/60">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0
          ${color === "amber" ? "bg-amber-100" : color === "orange" ? "bg-orange-100" : color === "rose" ? "bg-rose-100" : "bg-blue-100"}`}>
          <Icon className={`w-4.5 h-4.5
            ${color === "amber" ? "text-amber-600" : color === "orange" ? "text-orange-600" : color === "rose" ? "text-rose-500" : "text-blue-500"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <h4 className="text-sm font-bold text-gray-900">{label}</h4>
            {required && (
              <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full uppercase tracking-wide">
                Required
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">{sublabel}</p>
        </div>
        {selectedItems.length > 0 && (
          <span className="flex-shrink-0 text-xs font-semibold text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
            {selectedItems.length} selected
          </span>
        )}
      </div>

      <div className="px-5 py-4 space-y-4">
        {/* Selected chips */}
        {selectedItems.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {selectedItems.map((item) => (
              <Chip key={item} label={item} onRemove={() => onRemove(item)} color={color} />
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400 italic">Nothing selected yet — pick from suggestions or type your own below.</p>
        )}

        {/* Quick suggestions */}
        {filteredSuggestions.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
              {inputValue ? "Matching suggestions" : "Quick picks"}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {filteredSuggestions.slice(0, showSuggestions ? undefined : 8).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => handleAdd(s)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-all
                    ${c.suggestion}`}
                >
                  <Plus className="w-3 h-3 opacity-60" />
                  {s}
                </button>
              ))}
              {filteredSuggestions.length > 8 && !showSuggestions && (
                <button
                  type="button"
                  onClick={() => setShowSuggestions(true)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50 transition-all"
                >
                  +{filteredSuggestions.length - 8} more
                </button>
              )}
            </div>
          </div>
        )}

        {/* Custom input */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={`Custom ${label.toLowerCase()}…`}
              className={`w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-white
                focus:outline-none focus:ring-2 ${c.ring}
                placeholder:text-gray-400 transition-all`}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAdd(inputValue);
                }
              }}
            />
          </div>
          <button
            type="button"
            disabled={!inputValue.trim()}
            onClick={() => handleAdd(inputValue)}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold bg-[#FFD166] hover:bg-amber-400
              disabled:opacity-40 disabled:cursor-not-allowed text-gray-900 transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   Main component
══════════════════════════════════════════ */
const CuisineAndTags: React.FC<CuisineTagsStepProps> = ({ formData, updateFormData }) => {

  const handleAdd = (category: keyof RestaurantFormData, value: string) => {
    const current = (formData[category] as string[]) || [];
    if (!current.includes(value)) {
      updateFormData({ [category]: [...current, value] });
    }
  };

  const handleRemove = (category: keyof RestaurantFormData, value: string) => {
    const current = (formData[category] as string[]) || [];
    updateFormData({ [category]: current.filter((v) => v !== value) });
  };

  const totalSelected = CATEGORY_CONFIG.reduce((acc, cat) => {
    return acc + ((formData[cat.key] as string[]) || []).length;
  }, 0);

  return (
    <>
      <style>{`
        @keyframes chipIn {
          from { opacity: 0; transform: scale(0.85); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>

      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900 tracking-tight">Cuisine & Tags</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Help guests discover your restaurant with accurate categories and tags.
            </p>
          </div>
          {totalSelected > 0 && (
            <div className="flex-shrink-0 flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full">
              <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" />
              {totalSelected} total selected
            </div>
          )}
        </div>

        {/* Category sections */}
        {CATEGORY_CONFIG.map((cat) => {
          const selected = (formData[cat.key] as string[]) || [];
          return (
            <CategorySection
              key={cat.key as string}
              categoryKey={cat.key as string}
              label={cat.label}
              sublabel={cat.sublabel}
              icon={cat.icon}
              required={cat.required}
              color={cat.color}
              selectedItems={selected}
              onAdd={(value) => handleAdd(cat.key, value)}
              onRemove={(value) => handleRemove(cat.key, value)}
            />
          );
        })}
      </div>
    </>
  );
};

export default CuisineAndTags;